import { apiClient } from './api';

/**
 * Analytics Service
 * Handles all analytics-related API calls
 */
export const analyticsService = {
  /**
   * Get dashboard overview
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Start date (YYYY-MM-DD)
   * @param {string} params.end_date - End date (YYYY-MM-DD)
   * @param {number} params.account_id - Account ID (optional)
   * @returns {Promise} Overview data
   */
  getOverview: (params = {}) => {
    return apiClient.get('/analytics/overview', params);
  },

  /**
   * Get dashboard analytics with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} Dashboard analytics data
   */
  getDashboard: (params = {}) => {
    return apiClient.get('/analytics/dashboard', params);
  },

  /**
   * Get revenue summary
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue summary data
   */
  getRevenueSummary: (params = {}) => {
    return apiClient.get('/analytics/revenue/summary', params);
  },

  /**
   * Get revenue chart data
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue chart data
   */
  getRevenueChart: (params = {}) => {
    return apiClient.get('/analytics/revenue/chart', params);
  },

  /**
   * Get top apps
   * @param {Object} params - Query parameters
   * @returns {Promise} Top apps data
   */
  getTopApps: (params = {}) => {
    return apiClient.get('/analytics/top/apps', params);
  },

  /**
   * Get top ad units
   * @param {Object} params - Query parameters
   * @returns {Promise} Top ad units data
   */
  getTopAdUnits: (params = {}) => {
    return apiClient.get('/analytics/top/ad-units', params);
  },

  /**
   * Get top countries
   * @param {Object} params - Query parameters
   * @returns {Promise} Top countries data
   */
  getTopCountries: (params = {}) => {
    return apiClient.get('/analytics/top/countries', params);
  },

  /**
   * Get comparison data
   * @param {Object} params - Query parameters
   * @returns {Promise} Comparison data
   */
  getComparison: (params = {}) => {
    return apiClient.get('/analytics/comparison', params);
  },

  /**
   * Get revenue data
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue data
   */
  getRevenueData: (params = {}) => {
    return apiClient.get('/analytics/revenue', params);
  },

  /**
   * Get revenue trends
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue trends data
   */
  getRevenueTrends: (params = {}) => {
    return apiClient.get('/analytics/revenue/trends', params);
  },

  /**
   * Get revenue breakdown by app
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue breakdown by app
   */
  getRevenueBreakdownByApp: (params = {}) => {
    return apiClient.get('/analytics/revenue/breakdown/app', params);
  },

  /**
   * Get revenue breakdown by ad unit
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue breakdown by ad unit
   */
  getRevenueBreakdownByAdUnit: (params = {}) => {
    return apiClient.get('/analytics/revenue/breakdown/ad-unit', params);
  },

  /**
   * Get revenue breakdown by country
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue breakdown by country
   */
  getRevenueBreakdownByCountry: (params = {}) => {
    return apiClient.get('/analytics/revenue/breakdown/country', params);
  },

  /**
   * Get revenue breakdown by ad source
   * @param {Object} params - Query parameters
   * @returns {Promise} Revenue breakdown by ad source
   */
  getRevenueBreakdownByAdSource: (params = {}) => {
    return apiClient.get('/analytics/revenue/breakdown/ad-source', params);
  },

  /**
   * Get performance metrics data
   * @param {Object} params - Query parameters
   * @returns {Promise} Performance metrics data
   */
  getPerformanceMetrics: (params = {}) => {
    return apiClient.get('/analytics/performance', params);
  },

  /**
   * Get performance trends
   * @param {Object} params - Query parameters
   * @returns {Promise} Performance trends data
   */
  getPerformanceTrends: (params = {}) => {
    return apiClient.get('/analytics/performance/trends', params);
  },
};
